import { Inject, Injectable, InjectionToken, Renderer2, RendererFactory2 } from "@angular/core";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { Actions, Store, ofActionDispatched } from "@ngxs/store";
import { Subject, takeUntil } from "rxjs";
import { SoundDefinition, SoundType } from "../../models/sounds.models";
import { SoundsActions } from "../../state/sounds.actions";
import { SoundsState } from "../../state/sounds.state";
import { UIActions } from "../../state/ui.actions";

export const SOUND_DEFINITIONS_TOKEN = new InjectionToken<SoundDefinition>("SOUND_DEFINITIONS");
const FIRST_INTERACTION_EVENTS = ["click", "touchstart", "keydown"] as const;

@UntilDestroy()
@Injectable({
    providedIn: "root",
})
export class SoundService {
    private soundList: Map<SoundType, HTMLAudioElement> = new Map();
    private replaySoundsOnFirstInteractionList: Map<SoundType, boolean> = new Map();
    private renderer: Renderer2;
    private firstInteractionHandled$ = new Subject<void>();
    private firstInteractionErrorHandlerCallback: (() => void) | undefined;

    constructor(
        private readonly actions$: Actions,
        private readonly store: Store,
        @Inject(SOUND_DEFINITIONS_TOKEN) private readonly soundDefinition: SoundDefinition,
        private readonly rendererFactory: RendererFactory2
    ) {
        this.renderer = this.rendererFactory.createRenderer(null, null);
        this.actions$
            .pipe(ofActionDispatched(SoundsActions.PlaySound), untilDestroyed(this))
            .subscribe(({ soundType, shouldLoop, shouldReplayOnNoInteractionError }) =>
                this.playSound(soundType, shouldLoop, shouldReplayOnNoInteractionError)
            );

        this.actions$
            .pipe(ofActionDispatched(SoundsActions.StopSound), untilDestroyed(this))
            .subscribe(({ soundType }) => this.pauseSound(soundType, true));

        this.actions$
            .pipe(ofActionDispatched(SoundsActions.PauseSound), untilDestroyed(this))
            .subscribe(({ soundType }) => this.pauseSound(soundType));

        this.actions$
            .pipe(ofActionDispatched(SoundsActions.SoundPlayError), takeUntil(this.firstInteractionHandled$), untilDestroyed(this))
            .subscribe(
                ({ soundType, shouldLoopReplay, shouldReplayOnNoInteractionError }) =>
                    shouldReplayOnNoInteractionError && this.replaySoundsOnFirstInteractionList.set(soundType, shouldLoopReplay)
            );

        this.actions$
            .pipe(ofActionDispatched(SoundsActions.SetFirstInteractionErrorHandlerCallback), untilDestroyed(this))
            .subscribe(({ callback }) => {
                this.firstInteractionErrorHandlerCallback = callback;
            });

        this.handleErrorsBeforeUserFirstInteraction();
    }

    private playSound(soundType: SoundType, shouldLoop: boolean, shouldReplayOnNoInteractionError: boolean): void {
        if (!this.soundDefinition[soundType]) {
            return;
        }

        this.store.dispatch(UIActions.EnsureUserInteraction);

        const audioElement = this.soundList.get(soundType);
        const volume = this.store.selectSnapshot(SoundsState.systemVolume);

        if (!audioElement) {
            const newAudioElement = new Audio(this.soundDefinition[soundType]);
            newAudioElement.volume = volume;
            newAudioElement.loop = shouldLoop;
            this.soundList.set(soundType, newAudioElement);
            newAudioElement.addEventListener(
                "canplaythrough",
                this.canPlayThroughEventListenerWithPlayFactory(newAudioElement, soundType, shouldLoop, shouldReplayOnNoInteractionError)
            );

            return;
        }

        audioElement.volume = volume;
        audioElement.loop = shouldLoop;

        if (audioElement.readyState === HTMLMediaElement.HAVE_ENOUGH_DATA) {
            audioElement.play();

            return;
        }
        audioElement.addEventListener(
            "canplaythrough",
            this.canPlayThroughEventListenerWithPlayFactory(audioElement, soundType, shouldLoop, shouldReplayOnNoInteractionError)
        );
    }

    private pauseSound(soundType: SoundType, shouldRewindToStart: boolean = false): void {
        const audioElement = this.soundList.get(soundType);

        if (!audioElement) {
            return;
        }

        audioElement.pause();

        if (shouldRewindToStart) {
            audioElement.currentTime = 0;
        }
    }

    private canPlayThroughEventListenerWithPlayFactory(
        audio: HTMLAudioElement,
        soundType: SoundType,
        shouldReplayOnNoInteractionError: boolean,
        shouldLoop: boolean
    ) {
        const listenerRef = () => {
            audio.play().catch((error) => {
                this.store.dispatch(new SoundsActions.SoundPlayError(soundType, error, shouldReplayOnNoInteractionError, shouldLoop));
            });

            audio.removeEventListener("canplaythrough", listenerRef);
        };

        return listenerRef;
    }

    private handleErrorsBeforeUserFirstInteraction(): void {
        let isFirstInteractionHandled = false;

        FIRST_INTERACTION_EVENTS.forEach((event) => {
            const removeListener = this.renderer.listen("document", event, () => {
                removeListener();
                if (isFirstInteractionHandled) {
                    return;
                }

                if (this.firstInteractionErrorHandlerCallback) {
                    this.firstInteractionErrorHandlerCallback();
                } else {
                    this.replaySoundsOnFirstInteractionList.forEach((shouldLoop, soundType) => {
                        this.playSound(soundType, shouldLoop, false);
                    });
                }

                this.firstInteractionHandled$.next();
                this.firstInteractionHandled$.complete();
                this.replaySoundsOnFirstInteractionList.clear();
                isFirstInteractionHandled = true;
            });
        });
    }
}
